import { CheckCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { useRealtimeRun, useRealtimeStream } from "@trigger.dev/react-hooks";
import { Spin, Typography } from "antd";

import {
  TRAINEE_AI_REPORT_TEXT_STREAM_KEY,
  type TraineeAiReportProgress as TraineeAiReportProgressData,
} from "@fitness-app/data-models/entities/TraineeAiReport";

import { ReportMarkdown } from "./ReportMarkdown";

// Live view of one generation: the task mirrors its tool calls into the run
// metadata (`progress`) and pipes the report text into a realtime stream, so the
// trainer sees "Czytam czat…" → "Piszę raport" and the text growing, instead of a
// bare spinner. The DB row (realtime on trainee_ai_report) stays the source of
// truth for the final state — this is presentation only.

interface TraineeAiReportProgressProps {
  runId?: string;
  accessToken?: string;
}

const LiveProgress = ({ runId, accessToken }: { runId: string; accessToken: string }) => {
  const { run } = useRealtimeRun(runId, { accessToken });
  const { parts } = useRealtimeStream<string>(runId, TRAINEE_AI_REPORT_TEXT_STREAM_KEY, {
    accessToken,
    timeoutInSeconds: 300,
  });

  const progress = run?.metadata?.progress as TraineeAiReportProgressData | undefined;
  const steps = progress?.steps ?? [];
  const draft = parts?.join("") ?? "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        {steps.length === 0 ? (
          <Typography.Text type="secondary">
            <LoadingOutlined style={{ marginRight: 8 }} />
            Uruchamiam agenta…
          </Typography.Text>
        ) : (
          steps.map((step) => (
            <Typography.Text key={step.key} type={step.status === "done" ? "secondary" : undefined}>
              {step.status === "done" ? (
                <CheckCircleFilled style={{ color: "#52c41a", marginRight: 8 }} />
              ) : (
                <LoadingOutlined style={{ marginRight: 8 }} />
              )}
              {step.label}
            </Typography.Text>
          ))
        )}
      </div>
      {draft ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-4 opacity-80">
          <ReportMarkdown>{draft}</ReportMarkdown>
        </div>
      ) : null}
    </div>
  );
};

export const TraineeAiReportProgress = ({ runId, accessToken }: TraineeAiReportProgressProps) => {
  // The route stamps runId/accessToken right after dispatch — for a moment the row
  // is queued without them (or the token could not be minted at all).
  if (!runId || !accessToken) {
    return (
      <Typography.Text type="secondary">
        <Spin size="small" style={{ marginRight: 8 }} />
        Uruchamiam generowanie…
      </Typography.Text>
    );
  }

  return <LiveProgress runId={runId} accessToken={accessToken} />;
};
